import React, { useMemo } from 'react';
import { Box, TextField, InputAdornment, Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FaSearch, FaPlus } from 'react-icons/fa';
import { ReviewType, FeedbackSessionStatus } from '../../common/filters';
import { HeaderButton } from '../../common/styles';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';

const SessionFilters = ({
  searchTerm,
  onSearchChange,
  selectedStatuses,
  onStatusToggle,
  selectedReviewTypes,
  onReviewTypeToggle,
  selectedYear,
  onYearChange,
  selectedBusinessUnitId,
  onBusinessUnitChange,
  sessions = [],
  onNewSession,
}) => {
  const ability = useAbility();

  const yearOptions = useMemo(() => {
    const years = [...new Set(sessions.map(s => s.year).filter(Boolean))];
    return years.sort((a, b) => b - a);
  }, [sessions]);

  return (
    <Box sx={{
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flex: 1
      }}>
        <TextField
          placeholder="Search Sessions"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          autoComplete="off"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
            }
          }}
          sx={{
            width: '300px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              height: '40px',
              fontSize: '14px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              }
            },
            '& .MuiInputBase-input': {
              padding: '8px 14px',
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FeedbackSessionStatus
            selectedStatuses={selectedStatuses}
            onStatusToggle={onStatusToggle}
          />
        </Box>

        {/* Vertical Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '32px',
            mx: 1,
            borderColor: '#CBD5E1'
          }}
        />

        {/* Review Type Filters */}
        <ReviewType
          selectedReviewTypes={selectedReviewTypes}
          onReviewTypeToggle={onReviewTypeToggle}
        />

        {/* Vertical Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '32px',
            mx: 1,
            borderColor: '#CBD5E1'
          }}
        />

        {/* Year Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            label="Year"
            sx={{
              backgroundColor: 'white',
              height: '40px',
              fontSize: '14px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E2E8F0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              },
            }}
          >
            <MenuItem value="">All Years</MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={String(year)}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Business Unit Filter */}
        <BusinessUnitSelector
          value={selectedBusinessUnitId}
          onChange={onBusinessUnitChange}
          size="small"
          fullWidth={false}
          showEmptyOption
          emptyOptionLabel="All Business Units"
          label="Business Unit"
          showStructureType // ADR-119
          sx={{ minWidth: 200 }}
        />

      </Box>

      {ability.can(ACTIONS.CREATE, SUBJECTS.FEEDBACK_SESSION) && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={onNewSession}
            sx={{
              minWidth: 'auto',
              height: '32px',
              padding: '0 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              alignSelf: 'flex-start',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              }
            }}
          >
            <FaPlus style={{ marginRight: '8px' }} /> Schedule Session
          </HeaderButton>
        </Box>
      )}
    </Box>
  );
};

export default SessionFilters;
